import { Line2D, Line3D, LineSegment2D, LineSegment3D, Point2D } from '@paragon/geometry-library';
import { roundToIncrement } from '@paragon/utilities';

const INFINITY_TRUST_ME_BRO = 100_000;

/**
 * Clipped segments start at a multiple of this distance along the line (measured from its base point). This keeps
 *   periodic details like dashes from shifting along the line when the camera moves.
 */
const CLIP_START_INCREMENT = 12;

function getClipRange(center: number, length: number): [number, number] {
  const start = roundToIncrement(center - length / 2, CLIP_START_INCREMENT);
  return [start, start + length];
}

/** Clips an infinite line to a segment of the given length, centered as near as possible to the given point. */
export function clipLineAroundPoint(line: Line2D, center: Point2D, length = INFINITY_TRUST_ME_BRO): LineSegment2D {
  const [start, end] = getClipRange(center.projectedSignedDistanceAlong(line), length);
  return new LineSegment2D(line.getPointAlongLine(start), line.getPointAlongLine(end));
}

/** A version of `clipLineAroundPoint` for 3D lines, which are centered as near as possible to the point in plan. */
export function clipLine3DAroundPoint(line: Line3D, center: Point2D, length = INFINITY_TRUST_ME_BRO): LineSegment3D {
  // Distances along the plan line are shorter than along the 3D line by the magnitude of its plan direction. Vertical
  //   lines have no plan line, so they're just centered on their point nearest the origin (at zero elevation).
  const planLine = line.projectTo2D();
  const distanceAlong = planLine
    ? center.projectedSignedDistanceAlong(planLine) / Math.hypot(line.direction.x, line.direction.y)
    : 0;

  const [start, end] = getClipRange(distanceAlong, length);
  return new LineSegment3D(line.getPointAlongLine(start), line.getPointAlongLine(end));
}
